import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiClientError } from "@/lib/api-client";

export interface IceBreakerPrompt {
  question: string;
  payload?: string;
}

export interface IceBreakersData {
  prompts: IceBreakerPrompt[];
}

const queryKey = (brandId: string) => ["ig-icebreakers", brandId];

export function useIgIceBreakers(brandId: string | null | undefined) {
  return useQuery<IceBreakersData | null, ApiClientError>({
    queryKey: queryKey(brandId ?? ""),
    queryFn: async () => {
      try {
        return await apiFetch<IceBreakersData>(
          `/brands/${brandId}/inbox/ig-icebreakers`,
        );
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404 && err.code === "INBOX_DISABLED") {
          return null;
        }
        throw err;
      }
    },
    enabled: !!brandId,
    retry: false,
    staleTime: 30_000,
  });
}

export function useSaveIgIceBreakers(brandId: string) {
  const qc = useQueryClient();
  return useMutation<IceBreakersData, ApiClientError, IceBreakerPrompt[]>({
    mutationFn: (prompts) =>
      apiFetch<IceBreakersData>(`/brands/${brandId}/inbox/ig-icebreakers`, {
        method: "PUT",
        body: JSON.stringify({ prompts }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKey(brandId) });
    },
  });
}

export function useDeleteIgIceBreakers(brandId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ApiClientError, void>({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>(`/brands/${brandId}/inbox/ig-icebreakers`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.setQueryData(queryKey(brandId), { prompts: [] } satisfies IceBreakersData);
    },
  });
}
